document.addEventListener('DOMContentLoaded', function () {

  /* ================================================================
     THEME — LIVEFAST ecosystem JS
     Preserves all original functionality:
       cursor, nav scroll, active links, mobile nav, page transitions,
       scroll reveal, video hover previews, ambient music, AJAX cart.

     ── WHITE-SCREEN-ON-EXCEPTION BUG (fixed in a prior pass) ──
     This entire file used to be one flat function. Any single
     exception thrown midway (cursor code, ambient music, cart, a
     missing DOM node, a browser extension interfering, etc.) silently
     killed every block AFTER it for that page load — including scroll
     reveal init. Every major section on the site uses `.reveal`
     (opacity:0 by default, only flipped to visible by the
     IntersectionObserver set up below), so if that block never ran,
     the ENTIRE page stayed invisible. Fix: every block below is
     isolated in its own try/catch (safeRun), so one failure can never
     take down the rest of the page. Scroll reveal also gets a hard
     fallback in case IntersectionObserver itself is unavailable.

     ── REFRESH/FLASH BUG — ACTUAL ROOT CAUSE (fixed THIS pass) ──
     The exception isolation above was necessary but did not fix the
     bug people actually saw on plain refresh/direct URL/bookmark
     loads: a brief flash of the paper-colored curtain sweeping across
     the whole page on every load, not just in-app navigations.

     Root cause, confirmed by tracing event order: `pageshow` fires on
     EVERY page load — a fresh load, a hard refresh, AND a bfcache
     restore (`event.persisted === true`) — not only on bfcache
     restores as the old code assumed. The old code called `release()`
     unconditionally on every `pageshow`, and `release()` always does
     `curtain.classList.add('out')`. The `.curtain.out` keyframe
     (`cOut`) hard-codes its 0% frame to `transform: scaleY(1)` (fully
     covering) before animating down — so on a plain refresh, where the
     curtain was never covering anything, adding `.out` still forced a
     one-frame jump to "fully covering" and then animated it away,
     producing a visible flash on literally every reload, direct visit,
     and bookmark open, site-wide (every inner page has this — only
     the homepage has the Enter gate to mask it, and only while
     un-entered).

     The `cameFromNav` in-app-navigation reveal (a few lines below) was
     already scoped correctly with its own sessionStorage flag and
     never needed `pageshow` at all — it fires synchronously during
     this same DOMContentLoaded pass. The only legitimate job left for
     `pageshow` is a safety release for the rare case where a page gets
     bfcache-frozen mid-covering (curtain has `.in`, e.g. the user hit
     Back during the 250ms window between click and navigation). Fix:
     gate the `pageshow` handler on `curtain.classList.contains('in')`
     so it only ever acts when there's actually something to release.

     Also swapped the cleanup listener from `beforeunload` to
     `pagehide`: an unconditional `beforeunload` listener is a known
     bfcache blocker in Safari (and historically other engines), which
     works against the exact "cached page loads" failure mode this was
     supposed to guard against. `pagehide` does the same cleanup job
     without opting the page out of the cache.

     ── REFRESH/FLASH BUG — SECOND ROOT CAUSE (checkpoint 13) ──
     The fix above covers pageshow/bfcache correctly, but the flash
     could still happen through a different path: the `lf-navigating`
     sessionStorage flag used to be set immediately on click, a full
     250ms before the actual `window.location.href` navigation. Any
     interruption in that window (refresh, Back, a second nav attempt)
     left the flag behind — `pagehide`'s cleanup only ever reset the
     in-memory `navigating`/`navTimeout` state, never this flag — so it
     survived into whatever page loaded next and triggered an
     unwarranted sweep-away animation. Fixed by moving the flag-set
     next to the navigation it describes (inside the same setTimeout,
     immediately before `window.location.href`), and clearing it
     defensively inside `pagehide` too. See inline comments below.
  ================================================================ */

  function safeRun(label, fn) {
    try { fn(); } catch (e) { console.warn('[LF theme:' + label + ']', e); }
  }

  /* ── ONE-TIME INIT GUARD ──
     Prevents this file from double-binding listeners if it's ever
     included twice on the same page load. */
  if (window.__lfThemeBound) return;
  window.__lfThemeBound = true;

  /* ── PAGE CURTAIN (SAFETY-CRITICAL — kept first in init order) ──
     CHECKPOINT 26 — REVERTED an inline relocation of this block (and
     the Enter-gate / mobile-nav blocks below) that had been moved into
     layout/theme.liquid as raw <script> tags to bind earlier on slow
     connections. That relocation was never confirmed against a real
     browser, and the refresh bug returned in the build that shipped
     it. Rather than keep chasing an unverified difference between two
     versions that read identically on paper, this reverts to the
     exact architecture last confirmed bug-free (checkpoint 22b-2):
     one single controller, living here, wrapped in safeRun so a
     failure here can never take down any other block on the page. If
     the early-bind optimization is worth revisiting later, it should
     be re-added ONE block at a time, each verified live in a real
     browser against the checkpoint-22a test script before moving to
     the next, never all three at once again.

     Default .curtain CSS state is scaleY(0)/hidden/pointer-events:none,
     so even if this whole block throws, the curtain is never visible.

     - The reveal-sweep ('out' animation) only plays if THIS load was
       actually initiated by our own in-app navigation (flag set right
       before we change location.href). Plain refresh/direct URL/
       bookmark loads have no flag → curtain stays hidden, no animation.
     - Hard safety timeout on the 'in' (covering) state: if a
       navigation is triggered but location.href never actually takes
       the page away, the curtain auto-releases instead of blocking
       the UI forever. */
  safeRun('curtain', function () {
    const curtain = document.querySelector('.curtain');
    if (!curtain) return;

    let navigating = false;
    let navTimeout = null;
    let safetyTimer = null;

    function clearSafety() {
      if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
    }
    function release() {
      navigating = false;
      clearSafety();
      curtain.classList.remove('in');
      curtain.classList.add('out');
      setTimeout(() => curtain.classList.remove('out'), 450);
    }

    let cameFromNav = false;
    try { cameFromNav = sessionStorage.getItem('lf-navigating') === '1'; } catch (e) {}
    try { sessionStorage.removeItem('lf-navigating'); } catch (e) {}

    if (cameFromNav) {
      curtain.classList.add('out');
      setTimeout(() => curtain.classList.remove('out'), 450);
    }

    function isSafeLink(href, a) {
      if (!href) return false;
      if (href.startsWith('#')) return false;
      if (href.startsWith('http')) return false;
      if (href.startsWith('mailto')) return false;
      if (a.target === '_blank') return false;
      return true;
    }

    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a) return;

      const href = a.getAttribute('href');
      if (!isSafeLink(href, a)) return;

      e.preventDefault();

      if (navigating) return;
      navigating = true;

      if (navTimeout) clearTimeout(navTimeout);

      curtain.classList.remove('out');
      curtain.classList.add('in');

      clearSafety();
      safetyTimer = setTimeout(release, 1200);

      /* CORRECTION (checkpoint 13 — actual refresh-bug root cause #2):
         the sessionStorage flag used to be set here, immediately on
         click — a full 250ms before the actual navigation below. If
         anything interrupted that window (hit refresh, hit Back, a
         second nav attempt, network hiccup) the flag was left behind
         in sessionStorage. `pagehide` only ever cleared the in-memory
         `navigating`/`navTimeout` state, never this flag, so it
         survived into whatever page loaded next and incorrectly
         triggered the sweep-away animation on a curtain that was
         never actually covering — the exact flash bug, reached via a
         different path than the pageshow/bfcache one fixed earlier.
         Fix: set the flag right next to the navigation it describes
         (inside the same tick as `window.location.href`), and also
         clear it defensively in `pagehide` below as a second line of
         defense for any other interruption path. */
      navTimeout = setTimeout(() => {
        try { sessionStorage.setItem('lf-navigating', '1'); } catch (e) {}
        window.location.href = href;
      }, 250);
    });

    /* Only act if the curtain is actually mid-cover (a bfcache restore
       froze it with `.in` still applied, or the safety timer never
       got the chance to run before freeze). On a normal fresh load,
       hard refresh, or direct/bookmark visit, `.in` was never applied
       in the first place, so there is nothing to release and this is
       correctly a no-op — that's what stops the unwanted flash. */
    window.addEventListener('pageshow', () => {
      if (curtain.classList.contains('in')) release();
    });

    /* CHECKPOINT 22A — CONFIRMED RACE CONDITION, FIXED.
       pagehide used to unconditionally delete 'lf-navigating' "as a
       safety net" for interrupted navigations. But on every ORDINARY,
       successful nav (the common case, not an edge case), the actual
       sequence in a single tick is: (1) navTimeout fires → sets
       'lf-navigating' → sets location.href; (2) the browser starts
       tearing down this document for the new navigation → pagehide
       fires on THIS page; (3) pagehide's cleanup deletes the very
       flag just set one line above, before the incoming page's own
       script ever gets to read it. The incoming page reads-then-
       deletes that flag itself (see the cameFromNav block above) —
       that consume-once read already fully handles the "don't leak
       into a THIRD page" concern this cleanup was trying to solve,
       so having two different places clear the same flag was the
       actual race, not a fix for one. Removing the duplicate
       clear here means there is now exactly one writer (the click
       handler) and one reader/clearer (the next page's own load) —
       no more coin-flip between "did pagehide beat the new page to
       the flag." Also now clears the safety timer here (previously
       only cleared inside release()) so a bfcache-frozen page can
       never have a stale 1200ms timer resume mid-visit on a later
       restore and fire an unwanted curtain sweep. */
    window.addEventListener('pagehide', () => {
      navigating = false;
      if (navTimeout) clearTimeout(navTimeout);
      clearSafety();
    });
  });

  /* ── ENTER GATE (homepage only) ──
     A single Enter screen — LIVEFAST wordmark + one button, no 3-way
     room selector. No pre-paint script or class-based hiding of nav/
     content is used beyond the head FOUC-prevention script; the gate
     is a normal opaque full-screen element that paints immediately
     and is removed on click. A <noscript> rule in the markup hides it
     entirely if JS is disabled, so a broken/missing script can never
     leave it stuck. */
  safeRun('enter gate', function () {
    const gate = document.getElementById('gate-screen');
    if (!gate) return; // not the homepage

    let entered = false;
    try { entered = sessionStorage.getItem('lf-entered') === '1'; } catch (e) {}

    if (entered) {
      gate.style.display = 'none';
      return;
    }

    document.body.classList.add('gate-open');

    const enterBtn = document.getElementById('gate-enter');
    if (enterBtn) {
      enterBtn.addEventListener('click', () => {
        try { sessionStorage.setItem('lf-entered', '1'); } catch (e) {}
        gate.classList.add('leaving');
        document.body.classList.remove('gate-open');
        setTimeout(() => { gate.style.display = 'none'; }, 850);
      });
    }

    // subtle glow drift toward the cursor — desktop only, capped offset
    if (window.innerWidth > 768 && !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      const glow = gate.querySelector('.gate-glow');
      if (glow) {
        gate.addEventListener('mousemove', (e) => {
          const dx = (e.clientX / window.innerWidth - 0.5) * 36;
          const dy = (e.clientY / window.innerHeight - 0.5) * 36;
          glow.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        });
      }
    }
  });

  /* ── DARK MODE INIT (class applied before paint via inline script in <head>) ── */
  safeRun('dark mode', function () {
    const themeToggle = document.querySelector('.theme-toggle');
    function getTheme() { return document.documentElement.getAttribute('data-theme') || 'light'; }
    function syncToggleLabel(t) {
      if (themeToggle) themeToggle.title = t === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
    }
    function applyTheme(t) {
      document.documentElement.setAttribute('data-theme', t);
      try { localStorage.setItem('lf-theme', t); } catch (e) {}
      syncToggleLabel(t);
    }
    if (themeToggle) {
      /* CHECKPOINT 19: label used to only ever get set inside
         applyTheme(), which only ever runs on click — so the title
         attribute never matched the real theme on page load (it just
         sat on its static markup default until the first toggle).
         Sync it once here against whatever theme the head's pre-paint
         script already applied, same as every other page. */
      syncToggleLabel(getTheme());
      themeToggle.addEventListener('click', () => {
        applyTheme(getTheme() === 'dark' ? 'light' : 'dark');
      });
    }
  });

  /* ── CURSOR — retired this checkpoint (Redesign Checkpoint 1) ──
     The old JS-driven dot+ring lag cursor with BPM pulse is cut —
     see LFJ_MASTER_REDESIGN_BUILD_DOCUMENT.md, Part A3/A5. Replaced
     by a static custom cursor set purely in CSS (assets/lfj-pixel.css,
     cursor: url(...) on .lfj-pixel-scope). No JS needed for it at all;
     this is a real simplification, not just a reskin. */

  /* ── MAGNETIC BUTTONS ──
     Primary CTAs (and the Enter button) drift a few px toward the
     cursor while hovered, then ease back on mouseleave. Desktop only,
     capped offset so it stays subtle, and skipped entirely if the
     visitor has requested reduced motion. */
  safeRun('magnetic buttons', function () {
    if (window.innerWidth <= 768) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const STRENGTH = 0.25;   // fraction of cursor offset the button follows
    const MAX_OFFSET = 10;   // px cap so it never feels unhinged
    const LIFT = 2;          // preserves the existing -2px hover lift on primary/accent buttons

    function bindMagnetic(el, withLift) {
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const dx = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, (e.clientX - (r.left + r.width / 2)) * STRENGTH));
        const dy = Math.max(-MAX_OFFSET, Math.min(MAX_OFFSET, (e.clientY - (r.top + r.height / 2)) * STRENGTH));
        el.style.transform = `translate(${dx}px, ${dy - (withLift ? LIFT : 0)}px)`;
      });
      el.addEventListener('mouseleave', () => { el.style.transform = ''; });
    }

    document.querySelectorAll('.btn-primary, .btn-accent').forEach(el => bindMagnetic(el, true));
    document.querySelectorAll('.gate-enter-btn').forEach(el => bindMagnetic(el, false));
  });

  /* ── NAV SCROLL ── */
  safeRun('nav scroll', function () {
    const nav = document.querySelector('.site-nav');
    if (!nav) return;
    window.addEventListener('scroll', () => {
      nav.classList.toggle('scrolled', window.scrollY > 50);
    }, { passive: true });
  });

  /* ── ACTIVE NAV LINK ── */
  const path = window.location.pathname;
  safeRun('active nav link', function () {
    document.querySelectorAll('.nav-links a').forEach(a => {
      const href = a.getAttribute('href');
      if (href === path || (href !== '/' && path.startsWith(href))) a.classList.add('active');
    });
  });

  /* ── HIDDEN LINE (checkpoint 33) — touch equivalent ──
     Desktop relies on plain CSS :hover/:focus-visible. Touch devices
     have no hover, so the first tap reveals the label instead of
     navigating immediately; a second tap (or the same tap again once
     revealed) follows the link normally. Auto-hides the reveal after
     4s of no further interaction so it never gets stuck visibly open. */
  safeRun('hidden line touch reveal', function () {
    var el = document.querySelector('.lf-hidden-line');
    if (!el) return;
    var hoverCapable = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    if (hoverCapable) return; // desktop: CSS :hover already handles this, no JS needed

    el.addEventListener('click', function (e) {
      if (!el.classList.contains('lf-touch-reveal')) {
        e.preventDefault();
        el.classList.add('lf-touch-reveal');
        clearTimeout(el.__lfRevealTimer);
        el.__lfRevealTimer = setTimeout(function () {
          el.classList.remove('lf-touch-reveal');
        }, 4000);
      }
      /* else: reveal already showing, let this click navigate through */
    });
  });

  /* ── MOBILE NAV ── */
  safeRun('mobile nav', function () {
    const burger = document.querySelector('.hamburger');
    const mob = document.querySelector('.mobile-nav');
    const mclose = document.querySelector('.mobile-close');
    function openMob() {
      mob?.classList.add('open'); burger?.classList.add('open'); document.body.style.overflow = 'hidden';
      mob?.removeAttribute('inert'); mob?.setAttribute('aria-hidden', 'false');
      burger?.setAttribute('aria-expanded', 'true');
    }
    function closeMob() {
      mob?.classList.remove('open'); burger?.classList.remove('open'); document.body.style.overflow = '';
      mob?.setAttribute('inert', ''); mob?.setAttribute('aria-hidden', 'true');
      burger?.setAttribute('aria-expanded', 'false');
    }
    burger?.addEventListener('click', openMob);
    mclose?.addEventListener('click', closeMob);
    /* CHECKPOINT 20 — found while investigating the reported
       "refresh, then click a menu link, destination doesn't load in
       right" issue. This used to call closeMob() synchronously on the
       same click that also drives the curtain-navigation handler (a
       separate document-level 'click' listener above that reads
       e.target, calls preventDefault, and schedules window.location.href).
       closeMob() ran first (listener bound directly on the link fires
       before a listener on `document` during bubbling) and immediately
       set `inert` on `.mobile-nav` — an ancestor of the link still
       actively dispatching that same click event. Mutating an
       in-flight event's own ancestor mid-bubble is exactly the kind
       of thing that's inconsistent across browsers. Deferring the
       close to the next tick lets the curtain handler finish reading
       the event first, every time, with no visible delay to the
       visitor. */
    mob?.querySelectorAll('a').forEach(a => a.addEventListener('click', () => setTimeout(closeMob, 0)));
  });

  /* ── SCROLL REVEAL ──
     Hardened: falls back to instantly-visible if IntersectionObserver
     is unavailable, and force-reveals anything still hidden after a
     safety timeout no matter what (belt-and-suspenders against the
     white-screen failure mode described above). */
  safeRun('scroll reveal', function () {
    const revEls = document.querySelectorAll('.reveal');
    if (!revEls.length) return;

    if (!('IntersectionObserver' in window)) {
      revEls.forEach(el => el.classList.add('visible'));
      return;
    }

    const obs = new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('visible'); obs.unobserve(en.target); } });
    }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });
    revEls.forEach(el => obs.observe(el));

    // Safety net: whatever hasn't revealed itself in 1.2s (stuck
    // observer, zero-height container mid-transition, etc.) reveals
    // anyway rather than staying invisible forever. A faster, fully
    // inline safety net (independent of this file's own network
    // fetch) also runs at 800ms — see layout/theme.liquid, right
    // before this file loads. This one is the second-tier backstop
    // for the rare case where even that isn't enough.
    setTimeout(() => {
      document.querySelectorAll('.reveal:not(.visible)').forEach(el => el.classList.add('visible'));
    }, 1200);
  });

  /* ── VIDEO HOVER PREVIEWS ── */
  safeRun('video hover previews', function () {
    if (window.innerWidth <= 768) return;
    document.querySelectorAll('.video-card').forEach(card => {
      const prev = card.querySelector('.vthumb-video');
      if (!prev) return;
      let hoverTimer;
      card.addEventListener('mouseenter', () => { hoverTimer = setTimeout(() => prev.play().catch(() => {}), 300); });
      card.addEventListener('mouseleave', () => { clearTimeout(hoverTimer); prev.pause(); prev.currentTime = 0; });
    });
  });

  /* ── AMBIENT MUSIC (room-aware pools) ── */
  safeRun('ambient music', function () {
    const musicBtn = document.querySelector('.music-btn');
    const nowPlaying = document.querySelector('.now-playing');
    if (!musicBtn) return;

    let playing = false;
    let currentAudio = null;

    const songs = {
      all: [
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/Nine_Vicious_-_Love_Crazy_Instrumental.mp3?v=1776022892",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/Nine_Vicious_-_Italy_Instrumental.mp3?v=1776023167",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/Nine_Vicious_-_Coors_Light_Instrumental.mp3?v=1776023166",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/NINE_VICIOUS_-_MICHAEL_MYERS_prod._saintdadah.mp3?v=1781914207",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/Two_-Tone.mp3?v=1781914210",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/OLD_VICES._NEW_CITIES..mp3?v=1781914210",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/R8_Nine_Vicious_-_Free_Mind_Instrumental.mp3?v=1781914210",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/Ye.mp3?v=1781914209",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/Nine_Vicious_-_Face_V2_ahmad.mp3?v=1781914209",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/Nine_Vicious_-_Bag_bella.mp3?v=1781914207",
        "https://cdn.shopify.com/s/files/1/0793/0730/9297/files/NINE_VICIOUS_-_MICHAEL_MYERS_prod._saintdadah.mp3?v=1781914207"
      ]
    };
    songs.jaydo   = songs.all;
    songs.dvd     = songs.all;
    songs.culture = songs.all;

    function getRoomPool() {
      if (path.includes('/pages/jaydo')) return songs.jaydo;
      if (path.includes('/pages/dvd')) return songs.dvd;
      if (path === '/' || path.includes('/collections/merch') ||
          path.includes('/products/') || path.includes('/cart')) return songs.culture;
      return songs.all;
    }

    function startAmbient() {
      const pool = getRoomPool();
      const pick = pool[Math.floor(Math.random() * pool.length)];
      currentAudio = new Audio(pick);
      currentAudio.loop = true;
      currentAudio.volume = 0.15;
      currentAudio.play().catch(() => {});
    }
    function stopAmbient() {
      if (currentAudio) { currentAudio.pause(); currentAudio.currentTime = 0; currentAudio = null; }
    }

    musicBtn.addEventListener('click', () => {
      if (!playing) {
        startAmbient(); playing = true;
        musicBtn.classList.add('playing');
        nowPlaying?.classList.add('show');
      } else {
        stopAmbient(); playing = false;
        musicBtn.classList.remove('playing');
        nowPlaying?.classList.remove('show');
      }
    });
  });

  /* ── JTV BADGE (shown only on DVD page) ── */
  safeRun('jtv badge', function () {
    const jtvBadge = document.querySelector('.jtv-badge');
    if (jtvBadge && path.includes('/pages/dvd')) {
      jtvBadge.style.display = 'flex';
    }
  });

  /* ── AJAX ADD TO CART ── */
  safeRun('ajax add to cart', function () {
    document.querySelectorAll('.ajax-atc').forEach(btn => {
      btn.addEventListener('click', async function (e) {
        e.preventDefault(); e.stopPropagation();
        const id = this.dataset.variantId;
        if (!id) return;
        const orig = this.textContent;
        this.textContent = 'Adding…'; this.disabled = true;
        try {
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id, quantity: 1 })
          });
          if (r.ok) {
            this.textContent = 'Added ✓';
            const cart = await (await fetch('/cart.js')).json();
            const badge = document.querySelector('.cart-count');
            if (badge) {
              badge.textContent = cart.item_count;
            } else if (cart.item_count > 0) {
              const el = document.createElement('span');
              el.className = 'cart-count';
              el.textContent = cart.item_count;
              document.querySelector('.cart-icon')?.appendChild(el);
            }
          } else { this.textContent = 'Error'; }
        } catch (cartErr) { this.textContent = 'Try again'; console.warn('[LFJ cart]', cartErr); }
        setTimeout(() => { this.textContent = orig; this.disabled = false; }, 2000);
      });
    });
  });

});
